import { z } from 'zod'

// ─── Factuurreeks-gaten (FO-417) ─────────────────────────────────────────────
// Ontbrekende nummers in de verkoopfactuurreeks per BV per jaar (scope =
// InvoiceNumberSequence). Gedeeld tussen de API (routes/factuurreeks-gaten.ts,
// lib/factuurreeks-gaten.ts) en de backoffice-UI (/facturen?tab=reeks).

export const FACTUURREEKS_GAT_STATUS = ['OPEN', 'GERESERVEERD', 'GEACCEPTEERD', 'GESLOTEN'] as const
export type FactuurreeksGatStatus = (typeof FACTUURREEKS_GAT_STATUS)[number]

export const FACTUURREEKS_GAT_STATUS_LABELS: Record<FactuurreeksGatStatus, string> = {
  OPEN: 'Open',
  GERESERVEERD: 'Gereserveerd',
  GEACCEPTEERD: 'Geaccepteerd',
  GESLOTEN: 'Gesloten',
}

export const FACTUURREEKS_GAT_STATUS_BADGE_VARIANT: Record<
  FactuurreeksGatStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  OPEN: 'warning',
  GERESERVEERD: 'info',
  GEACCEPTEERD: 'secondary',
  GESLOTEN: 'success',
}

/** Bovengrens voor de vrije reden bij accepteren (verantwoording, geen roman). */
export const FACTUURREEKS_GAT_REDEN_MAX = 500

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén rij in het gaten-overzicht (/facturen?tab=reeks). */
export interface FactuurreeksGatRowDTO {
  id: string
  entityId: string
  entityName: string
  year: number
  volgnummer: number
  /** Het ontbrekende factuurnummer, bv. `FA202610103`. */
  invoiceNumber: string
  status: FactuurreeksGatStatus
  /** ISO-tijdstip van de eerste detectie. */
  gedetecteerdOp: string
  /** Verantwoording (alleen gevuld bij GEACCEPTEERD, blijft staan na sluiten). */
  geaccepteerdOp: string | null
  geaccepteerdDoor: { id: string; naam: string } | null
  reden: string | null
  /**
   * batch-63 (FO-471) — waarom het gat er is, als het systeem dat weet:
   * vóór het eerste nummer (startnummer controleren), factuur verwijderd of
   * hernummerd (bewust gat), of een sprong door een import.
   */
  aanleiding: string | null
  /** Reservering: het concept dat dit gat vult zodra het definitief wordt. */
  gereserveerdOp: string | null
  gereserveerdDoorInvoice: { id: string; nummer: string; verwijderd: boolean } | null
  /** Sluiting: de factuur die het nummer alsnog kreeg. */
  geslotenOp: string | null
  geslotenDoorInvoice: { id: string; invoiceNumber: string; verwijderd: boolean } | null
}

/** Telling per BV (+ jaar), server-side geteld over de volledige set. */
export interface FactuurreeksGatBvSamenvattingDTO {
  entityId: string
  entityName: string
  year: number
  open: number
  gereserveerd: number
  geaccepteerd: number
  gesloten: number
}

export interface FactuurreeksGatListMeta {
  total: number
  page: number
  pageSize: number
  /** Per BV × jaar de tellingen (onafhankelijk van status-filter en paginering). */
  perBv: FactuurreeksGatBvSamenvattingDTO[]
}

// ─── Request-schema's (gedeeld: API `.safeParse` + form-validatie) ───────────

/** Query van `GET /factuurreeks-gaten`. `status` weglaten = alle statussen. */
export const factuurreeksGatenListQuerySchema = z.object({
  entityId: z.string().min(1).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  status: z.enum(FACTUURREEKS_GAT_STATUS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})
export type FactuurreeksGatenListQuery = z.infer<typeof factuurreeksGatenListQuerySchema>

/** Body van `POST /factuurreeks-gaten/:id/accepteer`. */
export const factuurreeksGatAccepterenSchema = z.object({
  reden: z
    .string()
    .trim()
    .max(FACTUURREEKS_GAT_REDEN_MAX, `Reden mag maximaal ${FACTUURREEKS_GAT_REDEN_MAX} tekens zijn`)
    .optional(),
})
export type FactuurreeksGatAccepterenInput = z.infer<typeof factuurreeksGatAccepterenSchema>

/**
 * Body van `POST /factuurreeks-gaten/accepteer-bulk`. Twee vormen:
 *  - `ids`: een expliciete selectie (max 1000);
 *  - `entityId` + `year`: ALLE open gaten van die reeks (het "telling vooruit
 *    gezet"-blok, §3.1) - server-side, dus zonder client-cap.
 * Alleen OPEN gaten worden geraakt; de rest blijft ongemoeid.
 */
export const factuurreeksGatBulkAccepterenSchema = z.union([
  z.object({
    ids: z.array(z.string().min(1)).min(1, 'Selecteer minimaal één gat').max(1000),
    reden: z.string().trim().max(FACTUURREEKS_GAT_REDEN_MAX).optional(),
  }),
  z.object({
    entityId: z.string().min(1),
    year: z.number().int().min(2000).max(2100),
    reden: z.string().trim().max(FACTUURREEKS_GAT_REDEN_MAX).optional(),
  }),
])
export type FactuurreeksGatBulkAccepterenInput = z.infer<typeof factuurreeksGatBulkAccepterenSchema>

/** Antwoord van accepteer-bulk: hoeveel OPEN gaten daadwerkelijk zijn geaccepteerd. */
export interface FactuurreeksGatBulkResultDTO {
  geaccepteerd: number
}

/**
 * Voorzet voor "alsnog toevoegen": de import- en handmatig-flow krijgen het
 * gat mee zodat nummer + BV vast staan (`/facturen/import?gat=` en
 * `/facturatie?gat=`).
 */
export interface FactuurreeksGatVoorzetDTO {
  id: string
  entityId: string
  entityName: string
  year: number
  invoiceNumber: string
  status: FactuurreeksGatStatus
}

/** Meervoud-bewuste tekst voor titels/badges ("1 missend nummer" / "3 missende nummers"). */
export function factuurreeksGatAantalTekst(aantal: number): string {
  return aantal === 1 ? '1 missend nummer' : `${aantal} missende nummers`
}

// ─── batch-63 facturen-bronregel (FO-471) — reeksbewaking zonder stille overslag ─
// Classificatie van factuurnummers, het register van gesignaleerde afwijkingen
// (`FactuurreeksAfwijking`), per-BV extra nummerpatronen en het overzicht per
// BV x jaar. De losse gaten blijven in het FO-417-register hierboven.

/**
 * De vier weergaven van de Nummerreeks-tab (`/facturen?tab=reeks&weergave=…`).
 *
 * FO-476: deze lijst hoort hier en niet in de client-module met de
 * weergave-balk. De server-pagina leest de `?weergave=`-param met
 * `FACTUURREEKS_WEERGAVEN.includes(...)`; in de App Router is elke export van
 * een `'use client'`-module in een server component een client reference (een
 * proxy), en `.includes()` daarop gooit - precies waardoor de tab een foutkaart
 * gaf. Vanuit `@streep/types` is het weer een gewone array voor beide kanten.
 * De labels (`WEERGAVE_LABELS`) blijven in de client-module: die zijn puur UI.
 */
export const FACTUURREEKS_WEERGAVEN = ['overzicht', 'afwijkingen', 'gaten', 'patronen'] as const
export type FactuurreeksWeergave = (typeof FACTUURREEKS_WEERGAVEN)[number]

/** Classificatie van één factuurnummer ten opzichte van een reeks. */
export const FACTUURNUMMER_KLASSEN = [
  'CONFORM',
  'LEGACY_HERKEND',
  'AFWIJKEND_FORMAAT',
  'ANDERE_BV_CODE',
  'FINQLE',
  'CONCEPT',
] as const
export type FactuurnummerKlasse = (typeof FACTUURNUMMER_KLASSEN)[number]

export const FACTUURNUMMER_KLASSE_LABELS: Record<FactuurnummerKlasse, string> = {
  CONFORM: 'Conform (huidig formaat)',
  LEGACY_HERKEND: 'Oud formaat (herkend)',
  AFWIJKEND_FORMAAT: 'Afwijkend formaat',
  ANDERE_BV_CODE: 'Andere BV-code',
  FINQLE: 'Finqle-registratie',
  CONCEPT: 'Conceptnummer',
}

export const FACTUURREEKS_AFWIJKING_SOORTEN = [
  'BV_CODE_DRIFT',
  'REEKS_AFGELEID',
  'GEEN_EIGEN_REEKS',
  'AFWIJKEND_NUMMER',
  'DUPLICAAT',
  'SPRONG',
  'PLAFOND',
  'STARTNUMMER',
] as const
export type FactuurreeksAfwijkingSoort = (typeof FACTUURREEKS_AFWIJKING_SOORTEN)[number]

export const FACTUURREEKS_AFWIJKING_SOORT_LABELS: Record<FactuurreeksAfwijkingSoort, string> = {
  BV_CODE_DRIFT: 'BV-code wijkt af',
  REEKS_AFGELEID: 'Reeks afgeleid',
  GEEN_EIGEN_REEKS: 'Geen eigen reeks',
  AFWIJKEND_NUMMER: 'Afwijkend nummer',
  DUPLICAAT: 'Dubbel volgnummer',
  SPRONG: 'Sprong door import',
  PLAFOND: 'Nog niet verwerkt',
  STARTNUMMER: 'Startnummer controleren',
}

export const FACTUURREEKS_AFWIJKING_STATUSSEN = ['OPEN', 'VERKLAARD', 'OPGELOST'] as const
export type FactuurreeksAfwijkingStatus = (typeof FACTUURREEKS_AFWIJKING_STATUSSEN)[number]

export const FACTUURREEKS_AFWIJKING_STATUS_LABELS: Record<FactuurreeksAfwijkingStatus, string> = {
  OPEN: 'Open',
  VERKLAARD: 'Verklaard',
  OPGELOST: 'Opgelost',
}

export const FACTUURREEKS_AFWIJKING_STATUS_BADGE_VARIANT: Record<
  FactuurreeksAfwijkingStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  OPEN: 'warning',
  VERKLAARD: 'secondary',
  OPGELOST: 'success',
}

/** Acties die de server per afwijking aanbiedt (de UI doet geen eigen check). */
export const FACTUURREEKS_AFWIJKING_ACTIES = [
  'VERKLAREN',
  'REEKSCODE_BIJWERKEN',
  'ENTITEITSCODE_BIJWERKEN',
  'HERNUMMEREN',
  'BEREIK_ACCEPTEREN',
  'PATROON_TOEVOEGEN',
  'STARTNUMMER_BIJWERKEN',
] as const
export type FactuurreeksAfwijkingActie = (typeof FACTUURREEKS_AFWIJKING_ACTIES)[number]

/** Eén factuur (of Finqle-verzoeknummer) in de context van een afwijking. */
export interface FactuurreeksContextFactuurDTO {
  /** Factuur-id, of het verzoek-id bij herkomst FINQLE_VERZOEK. */
  id: string
  invoiceNumber: string
  /** ISO factuurdatum (of aanmaakdatum als er geen factuurdatum is). */
  datum: string | null
  verwijderd: boolean
  /** Waar het nummer vandaan komt: een factuurrij of een Finqle-verzoek. */
  herkomst: 'FACTUUR' | 'FINQLE_VERZOEK'
  /**
   * Alleen in de API-respons: waarom deze factuur niet hernummerd kan worden
   * (verzonden, betaald, Finqle-nummer, ...), of null als het wél kan.
   */
  hernummerBlokkade?: string | null
}

export interface FactuurreeksAfwijkingRowDTO {
  id: string
  soort: FactuurreeksAfwijkingSoort
  status: FactuurreeksAfwijkingStatus
  entityId: string
  entityName: string
  jaar: number | null
  reeksCode: string | null
  bereikVan: number | null
  bereikTot: number | null
  aantal: number | null
  invoice: { id: string; invoiceNumber: string | null; verwijderd: boolean } | null
  classificatie: FactuurnummerKlasse | null
  omschrijving: string
  /** Betrokken nummers met datum (begrensd; `contextAfgekapt` zegt of er meer is). */
  nummers: FactuurreeksContextFactuurDTO[]
  contextAfgekapt: boolean
  /** Voorstel in één zin (hernummeren / koppelen aan reeks / verklaren). */
  voorstel: string | null
  /** STARTNUMMER: het startnummer dat "startnummer bijwerken" zet (eerste gebruikte nummer). */
  startnummerVoorstel: number | null
  gevondenOp: string
  laatstGezienOp: string
  verklaring: string | null
  verklaardDoor: { id: string; naam: string } | null
  verklaardOp: string | null
  opgelostOp: string | null
  opgelostDoor: { id: string; naam: string } | null
  opgelostReden: string | null
  heropendOp: string | null
  /** Server-side bepaalde acties (rechten + staat). */
  acties: FactuurreeksAfwijkingActie[]
}

export interface FactuurreeksAfwijkingListMeta {
  total: number
  page: number
  pageSize: number
  /** Open afwijkingen binnen het BV-/jaar-filter, los van status/soort/paginering. */
  open: number
}

export const factuurreeksAfwijkingenListQuerySchema = z.object({
  entityId: z.string().min(1).optional(),
  jaar: z.coerce.number().int().min(2000).max(2100).optional(),
  status: z.enum(FACTUURREEKS_AFWIJKING_STATUSSEN).optional(),
  soort: z.enum(FACTUURREEKS_AFWIJKING_SOORTEN).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})
export type FactuurreeksAfwijkingenListQuery = z.infer<typeof factuurreeksAfwijkingenListQuerySchema>

/** Reden bij verklaren, herscannen en code-/nummercorrecties (verplicht, geauditeerd). */
const redenVerplicht = z
  .string({ error: 'Geef een reden op' })
  .trim()
  .min(3, 'Geef een reden op van minimaal 3 tekens')
  .max(FACTUURREEKS_GAT_REDEN_MAX, `Reden mag maximaal ${FACTUURREEKS_GAT_REDEN_MAX} tekens zijn`)

export const factuurreeksAfwijkingVerklarenSchema = z.object({ reden: redenVerplicht })
export type FactuurreeksAfwijkingVerklarenInput = z.infer<typeof factuurreeksAfwijkingVerklarenSchema>

export const factuurreeksAfwijkingBulkVerklarenSchema = z.object({
  ids: z.array(z.string().min(1)).min(1, 'Selecteer minimaal één afwijking').max(500),
  reden: redenVerplicht,
})
export type FactuurreeksAfwijkingBulkVerklarenInput = z.infer<
  typeof factuurreeksAfwijkingBulkVerklarenSchema
>

/** BV-code-drift oplossen: de reekscode óf de entiteitscode gelijktrekken. */
export const factuurreeksCodeBijwerkenSchema = z.object({
  richting: z.enum(['REEKSCODE', 'ENTITEITSCODE']),
  reden: redenVerplicht,
})
export type FactuurreeksCodeBijwerkenInput = z.infer<typeof factuurreeksCodeBijwerkenSchema>

/** Hernummeren van een afwijkend nummer naar een conform nummer (zonder verzend-/betaalsignalen). */
export const factuurreeksHernummerenSchema = z.object({
  /** Bij een dubbel volgnummer: welke van de facturen hernummerd wordt. */
  invoiceId: z.string().min(1).optional(),
  nieuwNummer: z
    .string({ error: 'Vul het nieuwe factuurnummer in' })
    .trim()
    .min(1, 'Vul het nieuwe factuurnummer in')
    .max(40, 'Een factuurnummer is maximaal 40 tekens'),
  reden: redenVerplicht,
})
export type FactuurreeksHernummerenInput = z.infer<typeof factuurreeksHernummerenSchema>

export interface FactuurreeksHernummerResultaatDTO {
  invoiceId: string
  van: string
  naar: string
  /** Eigen factuur met een opgeslagen PDF: die draagt nog het oude nummer. */
  pdfOpnieuwGenereren: boolean
  /** Leesbare zin over het effect op de teller van de reeks (of null). */
  reeksEffect: string | null
}

/** Een SPRONG-bereik in één keer als bewuste gaten accepteren. */
export const factuurreeksBereikAccepterenSchema = z.object({ reden: redenVerplicht })
export type FactuurreeksBereikAccepterenInput = z.infer<typeof factuurreeksBereikAccepterenSchema>

export interface FactuurreeksBereikResultaatDTO {
  geaccepteerd: number
}

/** STARTNUMMER oplossen: het startnummer van de reeks op het eerste gebruikte nummer zetten. */
export const factuurreeksStartnummerSchema = z.object({ reden: redenVerplicht })
export type FactuurreeksStartnummerInput = z.infer<typeof factuurreeksStartnummerSchema>

export interface FactuurreeksStartnummerResultaatDTO {
  van: number
  naar: number
  /** Open gaten vóór het nieuwe startnummer die daarmee verklaard zijn. */
  gatenVerklaard: number
}

/** Herscan (volledige doorrekening) met reden; optioneel beperkt tot één BV. */
export const factuurreeksHerscanSchema = z.object({
  reden: redenVerplicht,
  entityId: z.string().min(1).optional(),
})
export type FactuurreeksHerscanInput = z.infer<typeof factuurreeksHerscanSchema>

/** Export (CSV) van gaten of afwijkingen per BV x jaar. */
export const FACTUURREEKS_EXPORT_SOORTEN = ['gaten', 'afwijkingen'] as const
export type FactuurreeksExportSoort = (typeof FACTUURREEKS_EXPORT_SOORTEN)[number]

export const factuurreeksExportQuerySchema = z.object({
  soort: z.enum(FACTUURREEKS_EXPORT_SOORTEN),
  entityId: z.string().min(1).optional(),
  jaar: z.coerce.number().int().min(2000).max(2100).optional(),
  status: z.string().min(1).optional(),
  /** Alleen bij `soort=afwijkingen`: het soort-filter van de lijst. */
  afwijkingSoort: z.enum(FACTUURREEKS_AFWIJKING_SOORTEN).optional(),
})
export type FactuurreeksExportQuery = z.infer<typeof factuurreeksExportQuerySchema>

/** Een patroon deactiveren/verwijderen: altijd met reden (audit). */
export const factuurreeksPatroonVerwijderenSchema = z.object({ reden: redenVerplicht })
export type FactuurreeksPatroonVerwijderenInput = z.infer<
  typeof factuurreeksPatroonVerwijderenSchema
>

/** Vorm-check van een patroon (de API parseert het daarna echt). */
const PATROON_VORM = /^(?:[A-Za-z0-9\-/._ ]|\{jaar\}|\{code\}|\{volgnr(?::[1-9])?\})+$/

export const factuurreeksPatroonSchema = z
  .object({
    entityId: z.string({ error: 'Kies een BV' }).min(1, 'Kies een BV'),
    patroon: z
      .string({ error: 'Vul een patroon in' })
      .trim()
      .min(3, 'Vul een patroon in, bijvoorbeeld F{jaar}-{volgnr:4}')
      .max(60, 'Een patroon is maximaal 60 tekens')
      .regex(
        PATROON_VORM,
        'Gebruik letters, cijfers, - / . _ en de tokens {jaar}, {code}, {volgnr} of {volgnr:N}',
      )
      .refine((p) => p.includes('{volgnr'), 'Een patroon moet {volgnr} of {volgnr:N} bevatten'),
    omschrijving: z.string().trim().max(200).optional(),
    teltInReeks: z.boolean().default(false),
    actief: z.boolean().default(true),
    reden: redenVerplicht,
  })
  .refine((p) => !p.teltInReeks || p.patroon.includes('{jaar}'), {
    message: 'Een patroon dat meetelt in de reeks moet {jaar} bevatten',
    path: ['teltInReeks'],
  })
export type FactuurreeksPatroonInput = z.infer<typeof factuurreeksPatroonSchema>

export interface FactuurreeksPatroonDTO {
  /** Id van de rij; `ingebouwd:<n>` voor de vaste, niet-bewerkbare patronen. */
  id: string
  /** null = geldt voor elke BV (ingebouwd). */
  entityId: string | null
  entityName: string | null
  patroon: string
  omschrijving: string | null
  teltInReeks: boolean
  actief: boolean
  ingebouwd: boolean
}

/** BV-code-stand van een reeks. */
/**
 * `HISTORISCH`: een eerder jaar gebruikte een andere code dan de BV nu voert.
 * Dat is geschiedenis (nummers met die code horen bij hun reeks), geen actie;
 * alleen het lopende jaar en later zijn DRIFT (zelfde regel als de scan).
 */
export const FACTUURREEKS_BV_CODE_STANDEN = ['OK', 'DRIFT', 'HISTORISCH', 'GEEN_CODE', 'GEEN_REEKSRIJ'] as const
export type FactuurreeksBvCodeStand = (typeof FACTUURREEKS_BV_CODE_STANDEN)[number]

export const FACTUURREEKS_BV_CODE_STAND_LABELS: Record<FactuurreeksBvCodeStand, string> = {
  OK: 'Code klopt',
  DRIFT: 'Code wijkt af',
  HISTORISCH: 'Eerdere code',
  GEEN_CODE: 'BV heeft geen code',
  GEEN_REEKSRIJ: 'Geen reeksrij',
}

/** Eén rij in het overzicht per BV x jaar. */
export interface FactuurreeksOverzichtRijDTO {
  entityId: string
  entityName: string
  jaar: number
  /** Code van de reeksrij (null zonder reeksrij). */
  reeksCode: string | null
  /** Huidige `Entity.invoiceBvCode`. */
  entiteitCode: string | null
  bvCodeStand: FactuurreeksBvCodeStand
  startAt: number | null
  lastNumber: number | null
  volgendNummer: string | null
  /** Facturen van deze BV in dit jaar met een reeksnummer (incl. verwijderd). */
  aantalFacturen: number
  laagsteVolgnummer: number | null
  hoogsteVolgnummer: number | null
  openGaten: number
  gereserveerdeGaten: number
  geaccepteerdeGaten: number
  geslotenGaten: number
  afwijkendeNummers: number
  openAfwijkingen: number
  /** O7: gaten die het plafond van de laatste controle nog niet verwerkte. */
  nogNietVerwerkt: number
}

export interface FactuurreeksOverzichtDTO {
  rijen: FactuurreeksOverzichtRijDTO[]
  laatsteScan: { op: string; door: string | null; reden: string | null } | null
  patronen: FactuurreeksPatroonDTO[]
}

export interface FactuurreeksScanResultaatDTO {
  reeksen: number
  nieuweGaten: number
  geslotenGaten: number
  nieuweAfwijkingen: number
  opgelosteAfwijkingen: number
  heropendeAfwijkingen: number
  afgeleideReeksen: number
  nogNietVerwerkt: number
  gemeld: number
}

/** Meervoud-bewuste tekst voor de O7-melding. */
export function nogNietVerwerktTekst(aantal: number): string {
  return aantal === 1
    ? '1 gat nog niet verwerkt (volgende controle)'
    : `${aantal} gaten nog niet verwerkt (volgende controle)`
}
